import { flash, SubmitButton, Switch } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import { Formik } from "formik";
import { produce } from "immer";
import React from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { Experience, Permission } from "types";
import { api } from "../../api/api";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useInvalidate } from "../../hooks/useInvalidation";
import { TimePickerPopover } from "../Common/TimePickerPopover";
import { ContentLayout } from "../Layout/ContentLayout";

interface FormValues {
    startOffset: number;
    blockEntryOffset: number;
    payment: boolean;
}

interface ExperienceProps {
    experience: Experience;
}

interface VirtualMeetingPreference {
    enabled: boolean;
    startOffset: number;
    blockEntryOffset: number;
    payment: string;
}

export const enum PaymentPreference {
    Full = "full",
    None = "none",
}

export const initialValues: FormValues = {
    startOffset: 30,
    blockEntryOffset: 15,
    payment: true,
};

export const ExperienceVirtualMeeting = ({ experience }: ExperienceProps) => {
    const history = useHistory();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const virtualMeetingsEnabled = isGranted(Permission.VIRTUAL_MEETINGS_VIEW);

    const getInitialValues = (): FormValues => {
        const formvalues = experience.virtualMeeting;
        return produce(
            {
                ...initialValues,
            },
            (draft) => {
                if (formvalues) {
                    if (formvalues.startOffset) {
                        draft.startOffset = formvalues.startOffset;
                    }

                    if (formvalues.blockEntryOffset) {
                        draft.blockEntryOffset = formvalues.blockEntryOffset;
                    }

                    if (formvalues.payment) {
                        draft.payment = formvalues.payment === PaymentPreference.Full;
                    }
                }
            },
        );
    };

    const { mutateAsync: savePreference } = useMutation(async (payload: VirtualMeetingPreference) => {
        return api.post(`/experiences/${experience.id}/virtualMeeting`, payload);
    });

    const handleSubmit = async (values: FormValues) => {
        const payload = {
            enabled: false,
            startOffset: values.startOffset,
            blockEntryOffset: values.blockEntryOffset,
            payment: "",
        };
        if (virtualMeetingsEnabled) {
            payload.enabled = true;
        }

        payload.payment = values.payment ? PaymentPreference.Full : PaymentPreference.None;

        await savePreference(payload);
        flash.success("Virtual Meeting settings saved");
        invalidate("products", experience.id);
    };

    return (
        <Formik enableReinitialize initialValues={getInitialValues()} onSubmit={handleSubmit}>
            {({ values, setFieldValue, handleSubmit }) => {
                return (
                    <ContentLayout isWide>
                        <h2 className="mt-5">Virtual Meeting</h2>
                        <ContentLayout.Section title="Settings" description="Configure your virtual meeting settings">
                            <div className="flex flex-col space-y-6">
                                <div className="space-y-1">
                                    <span className="text-sm font-bold">Doors Open</span>
                                    <div className="flex items-center space-x-2.5">
                                        <span>
                                            Virtual meeting opens
                                            <TimePickerPopover
                                                showArrow
                                                value={values.startOffset}
                                                classNames={{ text: "px-0.5 pb-0.5 mx-1 !text-black !border-black" }}
                                                onApply={(value: number) => setFieldValue("startOffset", value)}
                                            />
                                            before scheduled time
                                        </span>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-sm font-bold">Doors Close</span>
                                    <div className="flex items-center space-x-2.5">
                                        <span>
                                            Prevent late guests from joining
                                            <TimePickerPopover
                                                showArrow
                                                classNames={{ text: "px-0.5 pb-0.5 mx-1 !text-black !border-black" }}
                                                value={values.blockEntryOffset}
                                                onApply={(value: number) => setFieldValue("blockEntryOffset", value)}
                                            />
                                            after scheduled time
                                        </span>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-sm font-bold">Payment</span>
                                    <div className="flex items-center space-x-2.5">
                                        <Switch
                                            size="medium"
                                            isChecked={values.payment}
                                            onChange={(value: boolean) => setFieldValue("payment", value)}
                                        />
                                        <span>Only guests who have paid in full are allowed to join</span>
                                    </div>
                                </div>
                            </div>
                        </ContentLayout.Section>

                        <ContentLayout.Footer preferenceSidebar>
                            <SubmitButton type="submit" size="medium" onClick={handleSubmit}>
                                Save
                            </SubmitButton>

                            <SecondaryButton
                                type="reset"
                                className="ml-3"
                                size="medium"
                                onClick={() => history.push("/products/experiences")}
                            >
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>
                    </ContentLayout>
                );
            }}
        </Formik>
    );
};
